import { api } from "./api";
import { imRegal, REGAL_ABLAGE } from "./datenbank";
import { nurEinNetzproblem } from "./warteschlange";

/**
 * Antworten der API auf dem Geraet, damit ohne Netz etwas dasteht.
 *
 * Ohne diese Ablage waere die Warteschlange die halbe Miete: der Mitarbeiter
 * koennte am Objekt zwar etwas eintragen, aber die Checkliste gar nicht erst
 * oeffnen, weil sie beim Aufruf vom Server kommt.
 *
 * Drei Festlegungen, die man nachlesen koennen soll:
 *
 * **Nur ausdrueckliche Pfade.** Abgelegt wird, was ein Aufrufer ausdruecklich
 * ueber diese Funktion holt, und das sind der Tag und der einzelne Bericht.
 * Wer alles ablegt, zeigt irgendwann einen Dienstplan von vorgestern an, ohne
 * dass jemand sieht, dass er alt ist.
 *
 * **Erst das Netz.** Es wird immer zuerst gefragt. Nur wenn die Anfrage am
 * Netz scheitert, kommt die Kopie zum Zug. Ein Fehler des Servers, etwa "kein
 * Zugriff", ist keine Funkluecke und wird durchgereicht.
 *
 * **Der Stand wird mitgeliefert.** Wer aus der Kopie liest, bekommt dazu den
 * Zeitpunkt. Die Oberflaeche schreibt ihn hin. Eine Anzeige, die nicht sagt,
 * dass sie alt ist, ist schlimmer als gar keine.
 */

interface Fach {
  pfad: string;
  daten: unknown;
  geholt: string;
}

export interface Antwort<T> {
  daten: T;
  /** Aus der Kopie auf dem Geraet, nicht vom Server. */
  ausAblage: boolean;
  /** Wann die Kopie geholt wurde. Nur gesetzt, wenn sie benutzt wurde. */
  stand: string | null;
}

export async function holenMitAblage<T>(pfad: string): Promise<Antwort<T>> {
  try {
    const daten = await api.get<T>(pfad);
    const fach: Fach = { pfad, daten, geholt: new Date().toISOString() };
    // Das Ablegen darf den Aufrufer nicht aufhalten und erst recht nicht
    // scheitern lassen: ein voller Speicher ist kein Grund, die Seite nicht
    // anzuzeigen.
    void imRegal(REGAL_ABLAGE, "readwrite", (r) => r.put(fach)).catch(() => {});
    return { daten, ausAblage: false, stand: null };
  } catch (err) {
    if (!nurEinNetzproblem(err)) throw err;
    const fach = await imRegal<Fach | undefined>(REGAL_ABLAGE, "readonly", (r) => r.get(pfad))
      .catch(() => undefined);
    if (!fach) throw err;
    return { daten: fach.daten as T, ausAblage: true, stand: fach.geholt };
  }
}

/**
 * Die Kopie von Hand nachfuehren.
 *
 * Wird gebraucht, wenn ohne Netz etwas geaendert wurde: der Eintrag steht in
 * der Warteschlange, die Kopie kennt ihn aber noch nicht. Ohne dieses
 * Nachfuehren zeigte die Seite nach einem Neuladen am Objekt wieder den alten
 * Stand an, und der Mitarbeiter traegt zum zweiten Mal dasselbe ein.
 */
export async function ablageAendern<T>(pfad: string, aendern: (alt: T) => T): Promise<void> {
  const fach = await imRegal<Fach | undefined>(REGAL_ABLAGE, "readonly", (r) => r.get(pfad))
    .catch(() => undefined);
  if (!fach) return;
  const neu: Fach = { ...fach, daten: aendern(fach.daten as T) };
  await imRegal(REGAL_ABLAGE, "readwrite", (r) => r.put(neu)).catch(() => {});
}
